// @flow
import React, { useState, useEffect, useReducer } from 'react';
import ApiCall from '../../../_base/functions/apicalls';
import { ILoader } from '~/_base/components/helixIcons';
import ApiAddresses from '../../../_base/constants/apiAddresses';
import Policy from '../../../_base/classes/policy';
import {
  Dialog,
  Typography,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Grid,
} from '@mui/material';

import MaterialFormGrid, {
  MaterialFormGridControl,
} from '../../../_base/components/materialFormGrid';
import { useToast } from '~/_base/context/toastContext';
import makeStyles from '@mui/styles/makeStyles';
import PasswordGuidelines from '~/_base/components/passwordGuidelines';
import { useTranslation } from 'react-i18next';

type Props = {
  onSave: () => void,
  onClose: () => void,
  open: boolean,
  id: number,
};

function userReducer(state, action) {
  switch (action.type) {
    case 'UPDATE_USER':
      return {
        ...state,
        ...action.payload,
      };
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

export default function ChangePasswordDialog(props: Props) {
  const classes = useStyles();
  const { warningToast, errorToast, successToast } = useToast();
  const [gettingData, setGettingData] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [userState, userDispatch] = useReducer(userReducer, {
    userObject: undefined,
    userPolicy: undefined,
  });
  const [shouldValidate, setShouldValidate] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    async function getUser(id: number) {
      let address = ApiAddresses({ name: 'user_data', id: id });

      try {
        const res = await ApiCall(address, 'GET');
        if (res.notice) {
          successToast(res.notice);
        }
        if (res.alert) {
          warningToast(res.alert);
        }
        setGettingData(false);

        userDispatch({
          type: 'UPDATE_USER',
          payload: {
            userObject: {
              ...res.entity,
              password_confirmation: '',
              current_password: '',
            },
            userPolicy: getUserPolicy(res.policy),
          },
        });
      } catch (err) {
        errorToast(err.message);
        setGettingData(false);
      }
    }
    props.open && getUser(props.id);
  }, [errorToast, props.id, props.open, successToast, warningToast]);

  function getUserPolicy(policy: any) {
    Policy.registerAttributeValidator(
      policy,
      'password_confirmation',
      (record, attribute, attributePolicy) => {
        if (
          record.password !== record.password_confirmation &&
          record.password_confirmation &&
          record.password
        ) {
          return [t("Password confirmation doesn't match Password")];
        }
        return [];
      }
    );
    return policy;
  }

  async function saveChange() {
    setShouldValidate(true);

    let user = userState.userObject;
    if (!user) return;

    let hasErrors = !Policy.isValid(user, userState.userPolicy);
    if (hasErrors) {
      warningToast(t('Please fix errors before saving'));
    } else {
      setSaving(true);
      // only need subset of user object for password change
      const objectToSave = {
        id: user.id,
        email: user.email,
        password: user.password,
        password_confirmation: user.password_confirmation,
        current_password: user.current_password,
      };
      let body = JSON.stringify({ user: objectToSave });
      let address = ApiAddresses({
        name: 'user_password_update',
        id: props.id,
      });
      try {
        const res = await ApiCall(address, 'PATCH', null, body);
        if (res.notice) {
          successToast(res.notice);
        }
        if (res.alert) {
          warningToast(res.alert);
        }
        setSaving(false);
        setShouldValidate(false);
        props.onSave();
      } catch (err) {
        errorToast(err.message);
        setSaving(false);
        setShouldValidate(false);
      }
    }
  }

  function updateUser(action: Object) {
    if (!userState.userObject) return;
    userDispatch({
      type: 'UPDATE_USER',
      payload: {
        userObject: { ...userState.userObject, ...action },
      },
    });
    setDirty(true);
  }

  let bindingProps = {
    source: userState.userObject,
    policy: userState.userPolicy,
  };
  return (
    <Dialog
      data-testid="change_password_dialog"
      open={props.open}
      onClose={props.onClose}
      PaperProps={{ style: { width: '500vw', maxWidth: '1000px' } }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h4" className={classes.title}>
          {t('Change password')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {gettingData ? (
          <ILoader fontSize="large" />
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={6}>
              <PasswordGuidelines />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <MaterialFormGrid
                {...bindingProps}
                onChange={(prop, val) => {
                  updateUser({ [prop]: val });
                }}
              >
                <MaterialFormGridControl
                  shouldValidate={shouldValidate}
                  size={12}
                  type="password"
                  label={t('Your password')}
                  prop="current_password"
                />
                <MaterialFormGridControl
                  shouldValidate={shouldValidate}
                  size={12}
                  type="password"
                  label={t('New password')}
                  prop="password"
                />
                <MaterialFormGridControl
                  shouldValidate={shouldValidate}
                  size={12}
                  type="password"
                  label={t('Confirm new password')}
                  prop="password_confirmation"
                />
              </MaterialFormGrid>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => {
            props.onClose();
            setShouldValidate(false);
          }}
        >
          {t('Cancel')}
        </Button>
        <Button
          sx={{ minWidth: '100px' }}
          startIcon={
            saving && (
              <ILoader fontSize="small" />
            )
          }
          disabled={!dirty}
          variant="contained"
          color="primary"
          onClick={e => saveChange()}
        >
          {t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    paddingLeft: '0',
    paddingRight: '0',
    paddingBottom: '0',
  },
  dialogActions: {
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(2),
  },
  title: {
    paddingLeft: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  titleActionContainer: {
    position: 'absolute',
    right: theme.spacing(2),
    top: theme.spacing(2),
  },
  tabRoot: {
    minWidth: '0',
  },
}));
